import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useContrato } from '@hooks/useContratos.js'
import { useContatos, telefoneParaWhatsApp } from '@hooks/useContatos.js'
import { db, type ContratoParcela, type ContratoStatus } from '@services/db.js'
import {
  deleteContrato,
  type MedicaoInput,
  type NovoContrato,
  type PagamentoParcelaInput,
} from '@services/gestao.js'
import { PageShell } from '@components/layout/PageShell.js'
import { Badge } from '@components/primitives/Badge.js'
import { Button } from '@components/primitives/Button.js'
import { Alert } from '@components/primitives/Alert.js'
import { EmptyPanel } from '@components/primitives/EmptyPanel.js'
import { Money } from '@components/primitives/Money.js'
import { CardListSkeleton, PageHeaderSkeleton } from '@components/primitives/Skeleton.js'
import { PagamentoBar } from '@components/contratos/PagamentoBar.js'
import { ParcelaItem } from '@components/contratos/ParcelaItem.js'
import { ParcelaFormModal } from '@components/contratos/ParcelaFormModal.js'
import { PagarParcelaModal } from '@components/contratos/PagarParcelaModal.js'
import { MedicaoModal } from '@components/contratos/MedicaoModal.js'
import { ContratoFormModal } from '@components/contratos/ContratoFormModal.js'
import {
  STATUS_LABEL,
  STATUS_VARIANT,
  TIPO_LABEL,
  mensagemWhatsAppParcela,
} from '@components/contratos/contratosLabels.js'
import { ROUTES, SINAL_MAXIMO_PCT } from '@utils/constants.js'
import { formatCurrency, formatDate } from '@utils/format.js'

export function ContratoDetailPage() {
  const { id: idParam } = useParams()
  const id = idParam ? Number(idParam) : null
  const navigate = useNavigate()
  const {
    item,
    isLoading,
    notFound,
    editar,
    adicionarParcela,
    editarParcela,
    removerParcela,
    liberar,
    desfazerLiberar,
    pagar,
    desfazerPagar,
  } = useContrato(id)
  const { todos: contatos } = useContatos()

  const [parcelaModal, setParcelaModal] = useState<{
    open: boolean
    parcela: ContratoParcela | null
  }>({ open: false, parcela: null })
  const [pagarParcela, setPagarParcela] = useState<ContratoParcela | null>(null)
  const [medicaoParcela, setMedicaoParcela] = useState<ContratoParcela | null>(null)
  const [editarOpen, setEditarOpen] = useState(false)
  const [confirmExcluir, setConfirmExcluir] = useState(false)
  const [flash, setFlash] = useState<string | null>(null)
  const [formas, setFormas] = useState<Record<number, string>>({})

  const lancamentoIds = useMemo(
    () =>
      (item?.parcelas ?? [])
        .map((p) => p.lancamentoId)
        .filter((v): v is number => typeof v === 'number'),
    [item],
  )

  useEffect(() => {
    if (lancamentoIds.length === 0) {
      setFormas({})
      return
    }
    let ativo = true
    db.lancamentos_financeiros
      .where('id')
      .anyOf(lancamentoIds)
      .toArray()
      .then((rows) => {
        if (!ativo) return
        const map: Record<number, string> = {}
        for (const r of rows) if (r.id != null && r.formaPagamento) map[r.id] = r.formaPagamento
        setFormas(map)
      })
    return () => {
      ativo = false
    }
  }, [lancamentoIds])

  useEffect(() => {
    if (!flash) return
    const t = window.setTimeout(() => setFlash(null), 4000)
    return () => window.clearTimeout(t)
  }, [flash])

  const contato = useMemo(
    () => (item?.contatoId != null ? contatos.find((c) => c.id === item.contatoId) : undefined),
    [contatos, item],
  )

  const mudarStatus = useCallback(
    async (status: ContratoStatus) => {
      await editar({ status })
    },
    [editar],
  )

  const excluir = useCallback(async () => {
    if (id == null) return
    await deleteContrato(id)
    navigate(ROUTES.CONTRATOS)
  }, [id, navigate])

  if (isLoading) {
    return (
      <PageShell>
        <PageHeaderSkeleton label='Carregando contrato…' />
        <section className='cf3-block'>
          <CardListSkeleton count={3} label='Carregando parcelas…' />
        </section>
      </PageShell>
    )
  }

  if (notFound || !item) {
    return (
      <PageShell>
        <EmptyPanel
          title='Contrato não encontrado'
          description='Ele pode ter sido excluído.'
          actionLabel='Voltar aos contratos'
          onAction={() => navigate(ROUTES.CONTRATOS)}
        />
      </PageShell>
    )
  }

  const { resumo } = item
  const ativo = item.status === 'ativo'
  const sinal = item.parcelas.find((p) => p.condicao === 'sinal')
  const sinalPct =
    sinal && item.valorTotal > 0 ? Math.round((sinal.valor / item.valorTotal) * 100) : 0
  const somaParcelas = resumo.valorParcelado
  const diferenca = item.valorTotal - somaParcelas
  const proximaAcao = ativo
    ? (item.parcelas.find((p) => p.status === 'liberada') ??
      item.parcelas.find((p) => p.status === 'prevista'))
    : undefined
  const wa = contato?.telefone ? telefoneParaWhatsApp(contato.telefone) : null
  const waMsg =
    proximaAcao && wa
      ? mensagemWhatsAppParcela({
          prestador: item.prestador,
          titulo: item.titulo,
          parcela: proximaAcao,
          valorFmt: formatCurrency(proximaAcao.valor),
        })
      : null

  async function medirEAvisar(parcelaId: number, medicao: MedicaoInput) {
    await liberar(parcelaId, medicao)
    setFlash('Serviço medido. Agora você pode pagar.')
    navigator.vibrate?.(20)
  }

  async function pagarEAvisar(parcelaId: number, input: PagamentoParcelaInput) {
    await pagar(parcelaId, input)
    setFlash('Pagamento lançado em Financeiro.')
    navigator.vibrate?.(20)
  }

  return (
    <PageShell>
      <nav className='cf3-gestao-trail' aria-label='Localização'>
        <Link to={ROUTES.CONTRATOS}>Contratos</Link>
        <span aria-hidden>·</span>
        <span>{item.titulo}</span>
      </nav>

      <header className='cf3-gestao-head'>
        <div className='cf3-gestao-head__text'>
          <h1 className='cf3-gestao-head__title'>{item.titulo}</h1>
          <p className='cf3-gestao-head__sub'>
            {item.prestador} · {TIPO_LABEL[item.tipo]}
            {item.faseLabel ? ` · ${item.faseLabel}` : ''}
          </p>
        </div>
        <Badge label={STATUS_LABEL[item.status]} variant={STATUS_VARIANT[item.status]} />
      </header>

      {flash && (
        <p className='cf3-flash' role='status'>
          {flash}
        </p>
      )}

      <section className='cf3-block' aria-label='Resumo do contrato'>
        <div className='cf3-block__card cf3-contrato-resumo'>
          <div className='cf3-contrato-resumo__top'>
            <span className='cf3-gestao-kpi__label'>Valor do contrato</span>
            <Money value={item.valorTotal} size={28} />
          </div>

          <PagamentoBar pctPago={resumo.pctPago} pctLiberado={resumo.pctLiberado} />

          <dl className='cf3-contrato-resumo__legend'>
            <div className='cf3-contrato-resumo__leg cf3-contrato-resumo__leg--pago'>
              <dt>Pago</dt>
              <dd>
                {formatCurrency(resumo.pago)} <span>({resumo.pctPago}%)</span>
              </dd>
            </div>
            <div className='cf3-contrato-resumo__leg cf3-contrato-resumo__leg--liberado'>
              <dt>Liberado</dt>
              <dd>
                {formatCurrency(resumo.liberado)} <span>({resumo.pctLiberado}%)</span>
              </dd>
            </div>
            <div className='cf3-contrato-resumo__leg'>
              <dt>A pagar</dt>
              <dd>{formatCurrency(resumo.aPagar)}</dd>
            </div>
          </dl>

          {(item.dataInicio || item.dataFimPrevista) && (
            <p className='cf3-contrato-resumo__datas'>
              {item.dataInicio ? `Início ${formatDate(item.dataInicio)}` : ''}
              {item.dataInicio && item.dataFimPrevista ? ' · ' : ''}
              {item.dataFimPrevista ? `Fim previsto ${formatDate(item.dataFimPrevista)}` : ''}
            </p>
          )}

          {(contato?.telefone || waMsg) && (
            <div className='cf3-contrato-resumo__contato'>
              {contato?.telefone && (
                <a
                  className='cf3-contato__acao'
                  href={`tel:${contato.telefone.replace(/\D/g, '')}`}
                >
                  Ligar para {contato.nome.split(' ')[0]}
                </a>
              )}
              {wa && waMsg && (
                <a
                  className='cf3-contato__acao cf3-contato__acao--wa'
                  href={`https://wa.me/${wa}?text=${encodeURIComponent(waMsg)}`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Avisar no WhatsApp
                </a>
              )}
            </div>
          )}
        </div>

        {ativo && resumo.adiantamentoPct > 0 && (
          <Alert
            variant={resumo.adiantamentoPct > SINAL_MAXIMO_PCT ? 'danger' : 'warning'}
            title={`Você pagou ${resumo.adiantamentoPct}% além do serviço liberado`}
            message='Segure os próximos pagamentos até medir o que já foi feito. Dinheiro adiantado é a maior alavanca de abandono de obra.'
          />
        )}

        {ativo && sinalPct > SINAL_MAXIMO_PCT && (
          <Alert
            variant='warning'
            title={`Sinal de ${sinalPct}%`}
            message={`Acima do limite seguro de ${SINAL_MAXIMO_PCT}%. Se ainda não pagou, renegocie para 10 a 15%.`}
          />
        )}

        {Math.abs(diferenca) >= 1 && (
          <p className='cf3-gestao-aviso'>
            {diferenca > 0
              ? `Parcelas somam ${formatCurrency(somaParcelas)}: faltam ${formatCurrency(diferenca)} para fechar o valor do contrato.`
              : `Parcelas somam ${formatCurrency(somaParcelas)}: ${formatCurrency(Math.abs(diferenca))} acima do contrato (aditivos?).`}
          </p>
        )}

        {item.escopo && (
          <details className='cf3-gestao-details'>
            <summary className='cf3-gestao-details__summary'>Escopo combinado</summary>
            <p className='cf3-gestao-details__body'>{item.escopo}</p>
          </details>
        )}
      </section>

      <section className='cf3-block' aria-label='Parcelas'>
        <div className='cf3-gestao-section-head'>
          <h2 className='cf3-subsection-title'>Parcelas</h2>
          {ativo && (
            <Button
              variant='secondary'
              size='sm'
              onClick={() => setParcelaModal({ open: true, parcela: null })}
            >
              Adicionar
            </Button>
          )}
        </div>

        {proximaAcao && (
          <div className='cf3-proximo'>
            <p className='cf3-proximo__texto'>
              {proximaAcao.status === 'liberada' ? (
                <>
                  Próximo: pagar <strong>{proximaAcao.descricao}</strong> (
                  {formatCurrency(proximaAcao.valor)})
                </>
              ) : (
                <>
                  Próximo: medir <strong>{proximaAcao.descricao}</strong> no canteiro (
                  {formatCurrency(proximaAcao.valor)})
                </>
              )}
            </p>
            {proximaAcao.status === 'liberada' ? (
              <Button variant='primary' size='sm' onClick={() => setPagarParcela(proximaAcao)}>
                Registrar pagamento
              </Button>
            ) : (
              <Button variant='primary' size='sm' onClick={() => setMedicaoParcela(proximaAcao)}>
                Medir agora
              </Button>
            )}
          </div>
        )}

        {item.parcelas.length === 0 ? (
          <EmptyPanel
            title='Sem parcelas'
            description='Divida o valor em sinal, marcos de medição e retenção final.'
            actionLabel='Adicionar parcela'
            onAction={() => setParcelaModal({ open: true, parcela: null })}
          />
        ) : (
          <ol className='cf3-parcelas'>
            {item.parcelas.map((p) => (
              <ParcelaItem
                key={p.id}
                parcela={p}
                contratoAtivo={ativo}
                {...(p.lancamentoId != null && formas[p.lancamentoId]
                  ? { formaPagamento: formas[p.lancamentoId] as string }
                  : {})}
                onMedir={setMedicaoParcela}
                onDesfazerLiberar={desfazerLiberar}
                onPagar={setPagarParcela}
                onDesfazerPagar={desfazerPagar}
                onEditar={(parcela) => setParcelaModal({ open: true, parcela })}
                onRemover={removerParcela}
              />
            ))}
          </ol>
        )}
      </section>

      <section className='cf3-block' aria-label='Ações do contrato'>
        <div className='cf3-gestao-actions'>
          <Button variant='secondary' size='sm' onClick={() => setEditarOpen(true)}>
            Editar contrato
          </Button>
          {ativo ? (
            <>
              <Button variant='ghost' size='sm' onClick={() => mudarStatus('concluido')}>
                Marcar concluído
              </Button>
              <Button variant='ghost' size='sm' onClick={() => mudarStatus('cancelado')}>
                Cancelar contrato
              </Button>
            </>
          ) : (
            <Button variant='ghost' size='sm' onClick={() => mudarStatus('ativo')}>
              Reabrir contrato
            </Button>
          )}
          {!confirmExcluir ? (
            <Button variant='ghost' size='sm' onClick={() => setConfirmExcluir(true)}>
              Excluir
            </Button>
          ) : (
            <>
              <Button variant='danger' size='sm' onClick={excluir}>
                Confirmar exclusão
              </Button>
              <Button variant='ghost' size='sm' onClick={() => setConfirmExcluir(false)}>
                Cancelar
              </Button>
            </>
          )}
        </div>
        {confirmExcluir && (
          <p className='cf3-gestao-aviso'>
            Os lançamentos já pagos permanecem no Financeiro. Só o contrato e as parcelas somem.
          </p>
        )}
      </section>

      <ParcelaFormModal
        open={parcelaModal.open}
        initial={parcelaModal.parcela}
        onClose={() => setParcelaModal({ open: false, parcela: null })}
        onSalvar={adicionarParcela}
        onEditar={(parcelaId, patch) => editarParcela(parcelaId, patch)}
      />

      <PagarParcelaModal
        open={pagarParcela !== null}
        parcela={pagarParcela}
        prestador={item.prestador}
        onClose={() => setPagarParcela(null)}
        onPagar={pagarEAvisar}
      />

      <MedicaoModal
        open={medicaoParcela !== null}
        parcela={medicaoParcela}
        prestador={item.prestador}
        onClose={() => setMedicaoParcela(null)}
        onLiberar={medirEAvisar}
      />

      <ContratoFormModal
        open={editarOpen}
        initial={item}
        contatos={contatos}
        onClose={() => setEditarOpen(false)}
        onSalvar={async () => undefined}
        onEditar={(_, patch: Partial<NovoContrato>) => editar(patch)}
      />
    </PageShell>
  )
}
